// Annual Reports Types

export interface AdditionalFee {
  id: string | null;
  order: number;
  description: string;
  per_member_fee: number | null;
  per_chapter_fee: number | null;
  delete?: boolean;
}

export interface SPDPreworkListItem {
  id: string;
  year: number;
  spd_chapter_id: string;
  spd_chapter_name: string;
  status_id: string;
  status_name: string;
  submitted_at: string | null;
  submitted_by_name: string | null;
}

export interface SPDPreworkForm {
  id: string;
  year: number;
  spd_chapter_id: string;
  spd_chapter_name: string;
  spd_chapter_abbreviation: string;
  status_id: string;
  status_name: string;
  submitted_at: string | null;
  submitted_by_id: string | null;
  submitted_by_name: string | null;
  state_dues: number | null;
  remit_to_name: string | null;
  first_approver_id: string | null;
  first_approver_name: string | null;
  second_approver_id: string | null;
  second_approver_name: string | null;
  check_payable_to: string | null;
  address_1: string | null;
  address_2: string | null;
  city: string | null;
  state_id: string | null;
  state_name: string | null;
  postal_code: string | null;
  country_id: string | null;
  country_name: string | null;
  approved_by_id: string | null;
  approved_by_name: string | null;
  additional_fees: AdditionalFee[];
}

export interface SPDPreworkUpdateRequest {
  state_dues?: number;
  remit_to_name?: string;
  first_approver_id?: string;
  second_approver_id?: string;
  check_payable_to?: string;
  address_1?: string;
  address_2?: string;
  city?: string;
  state_id?: string;
  postal_code?: string;
  country_id?: string;
  additional_fees?: AdditionalFee[];
  mark_complete: boolean;
}

export interface ApprovalActionRequest {
  action: 'approve' | 'send_back' | 'unapprove';
}

// Status IDs from backend
export const SPDPreworkStatus = {
  NOT_STARTED: 'c5ed2f02d72949fea17d068f57155d4c',
  IN_PROGRESS: '1aca4c15340846b780a9e65f807f838d',
  READY_FOR_REVIEW: '1fb7d5e89895431490ca49e997e79be5',
  APPROVED: '532ff38163284dac926f5248cf4b1d2b',
} as const;

export type SPDPreworkStatusType =
  (typeof SPDPreworkStatus)[keyof typeof SPDPreworkStatus];

// Dashboard Types
export interface AnnualReportsDashboardResponse {
  persona: 'local_chapter' | 'spd_chapter' | 'international';
  year: number;
  notifications: NotificationsSection;
  local_chapter?: LocalChapterDashboard;
  overview?: OverviewDashboard;
}

export interface NotificationsSection {
  assigned_to_me: FormNotification[];
  needs_attention: FormNotification[];
  type_totals?: Record<string, number>; // Total count per form_type
}

export interface FormNotification {
  form_type:
    | 'roster_dues'
    | 'roster_dues_lc'
    | 'roster_dues_spd'
    | 'irs_lc'
    | 'irs_sc'
    | 'irs_rg';
  form_id: string;
  form_name: string;
  status_id: string;
  status_name: string;
  assigned_to_id?: string;
  assigned_to?: string;
  link: string;
  priority?: string;
}

export interface LocalChapterDashboard {
  chapter_id: string;
  chapter_name: string;
  roster_dues?: FormStatus;
  irs_lc?: FormStatus;
}

export interface FormStatus {
  form_id: string;
  status_id: string;
  status_name: string;
  assigned_to_id?: string;
  assigned_to?: string;
  can_edit: boolean;
  last_updated: string;
  link: string;
}

export interface OverviewDashboard {
  spd_chapter_id?: string;
  spd_chapter_name?: string;
  roster_dues_lc: FormTypeSummary; // Local Chapter Roster & Dues
  roster_dues_spd: FormTypeSummary; // SPD Chapter Roster & Dues
  irs_lc: FormTypeSummary;
  irs_sc: FormTypeSummary;
  irs_rg: FormTypeSummary;
}

export interface FormTypeSummary {
  total_count: number;
  status_breakdown: StatusCount[];
  link: string;
}

export interface StatusCount {
  status_id: string;
  count: number;
  // status_name and display_order will be added on frontend from variables.js
  status_name?: string;
  display_order?: number;
}
